import {
  Box,
  MenuItem,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import { fetchScopedCentres } from 'api'
// The (i) SOP button and its category list live under Procurement, but neither
// is procurement-specific — the Cash Register reuses them so Finance edits this
// page's SOP from the same HQ -> SOP tab as every other one.
import SopButton from 'app/pages/Procurement/components/SopButton'
import { orderCentres } from 'app/pages/Procurement/constants'

import Ledger from './Ledger'

// Top-level Cash Register page (user 2026-08-27: its own sidebar entry, just
// above Centre Operations — moved out of Procurement & Expenses). The shell
// owns the centre picker, same contract as Procurement's Center Team page:
// scoped centres, choice persisted, the ledger receives a controlled centreId.
const CENTRE_KEY = 'cashRegister.centre'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  title: { fontWeight: 700 },
  sub: { color: '#777', fontSize: 13 },
  select: { minWidth: 220 }
}))

const CashRegisterPage = () => {
  const classes = useStyles()
  const [centres, setCentres] = React.useState([])
  const [centreId, setCentreId] = React.useState(
    () => window.localStorage.getItem(CENTRE_KEY) || ''
  )

  React.useEffect(() => {
    ;(async () => {
      try {
        // Server-side centre-scoped (NOT api/procurement's fetchMyCentres,
        // which returns every centre despite the name).
        const res = await fetchScopedCentres()
        const list = orderCentres(res?.results || [])
        setCentres(list)
        setCentreId((prev) =>
          prev === 'all' || (prev && list.some((c) => c.uuid === prev))
            ? prev
            : list[0]?.uuid || ''
        )
      } catch (e) {
        /* the ledger surfaces its own load errors */
      }
    })()
  }, [])

  React.useEffect(() => {
    if (centreId) window.localStorage.setItem(CENTRE_KEY, centreId)
  }, [centreId])

  return (
    <Box className={classes.root}>
      <div className={classes.topRow}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Typography variant="h6" className={classes.title}>
              Cash Register
            </Typography>
            <SopButton category="cash_register" />
          </div>
          <Typography className={classes.sub}>
            {centreId === 'all'
              ? 'Cash and cheques in hand across every centre you can see. Open a single centre to count or settle.'
              : 'Cash and cheques collected at the centre — get them to the bank, challan attached.'}
          </Typography>
        </div>
        <TextField
          select
          className={classes.select}
          size="small"
          variant="outlined"
          label="Centre"
          value={centreId}
          onChange={(e) => setCentreId(e.target.value)}
        >
          {/* Answers "how much is out there across my centres" without
              opening each one. Read-only — a deposit belongs to a single
              centre, so nothing can be settled from this view. */}
          {centres.length > 1 && <MenuItem value="all">All centres</MenuItem>}
          {centres.map((c) => (
            <MenuItem key={c.uuid} value={c.uuid}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      </div>
      {centreId ? (
        <Ledger centreId={centreId} />
      ) : (
        <Box p={4}>
          <Typography variant="body2" color="textSecondary">
            {centres.length
              ? 'Select a centre to continue.'
              : 'Loading centres…'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default CashRegisterPage
